<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/ui-plus/sonner";
import { authState } from '@/ui-plus/useAuth';
import { authorize } from '@auth-frontend-vue/lib/auth.actions';
import UserCard from "@/ui-plus/UserCard.vue";
import { Loader2 } from "lucide-vue-next";
import { startRefreshingTokens } from '@api-client/auth/auth';

const isAuthorizing = ref(false);

const showErrorToast = () => {
    toast.error("Unable to get redirect url", {
        duration: 600_000,
        description: "Please go back to the previous website then try again",
        action: {
            label: "Go back",
            onClick: () => {
                window.history.back();
            }
        }
    });
};

const authorizeNow = async () => {
    const redirectUrl = window.queryParams.next;
    if (!redirectUrl) {
        showErrorToast();
        return;
    }
    const { pkce_code_challenge, pkce_state } = window.queryParams;

    // If PKCE params present, do authorization flow
    if (pkce_code_challenge && pkce_state) {
        if (isAuthorizing.value) return;
        isAuthorizing.value = true;

        const { data, error } = await authorize({
            code_challenge: pkce_code_challenge,
            redirect_uri: redirectUrl,
            state: pkce_state,
        });

        if (error) {
            isAuthorizing.value = false;
            toast.error("Authorization maybe expired please login again");
            await startRefreshingTokens(); // check it can be refreshed
            return;
        }

        // Redirect back to the parent with auth code in hash (not sent to server / Referer)
        const url = new URL(redirectUrl);
        const hashParams = new URLSearchParams(url.hash.slice(1));
        hashParams.set('zmg_code', data.code);
        hashParams.set('zmg_state', pkce_state);
        url.hash = hashParams.toString();
        window.location.href = url.toString();
        return;
    }
    toast.info("click back button to go back to the previous website");
};

// const openSettings = async () => {
//     window.location.href = window.location.href.replace('type=ask', 'type=settings')
// }

const nextHostname = new URL(window.queryParams.next).hostname;

onMounted(() => {
    setTimeout(() => {
        if (import.meta.env.PUBLIC_AUTH_AUTO_REDIRECT === "1") {
            authorizeNow();
        } else {
            toast.success("Click continue to use your account", {
                // description: ""
            });
        }
    }, 100);
});
</script>

<template>
    <Card>
        <CardContent class="my-6">
            <UserCard :user="authState.accessTokenPayload" />
            <!-- <Button @click="openSettings"
                    variant="outline"
                    class="mt-6 w-full font-medium">
                Settings
            </Button> -->
            <div class="mt-6 text-center"
                 v-if="isAuthorizing">
                <Loader2 class="animate-spin mx-auto" />
                Redirecting you to <strong>{{ nextHostname }}</strong>...
            </div>
            <Button v-else
                    class="mt-6 w-full font-medium continue-button max-w-full"
                    @click="authorizeNow">
                Continue
                <span class="font-bold underline line-clamp-1">{{ nextHostname }}</span>
            </Button>
        </CardContent>
    </Card>
</template>
